"use client";

import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { useContent } from "@/components/site/ContentProvider";
import type { SiteCaseStudy } from "@/content/schema";
import styles from "./expandable-cases.module.css";

const EASE = [0.22, 0.61, 0.36, 1] as const;
const LAYOUT_TRANSITION = { duration: 0.28, ease: EASE };

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.closeGlyph}
      aria-hidden
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function lockPageScroll(lock: boolean) {
  const html = document.documentElement;
  const body = document.body;
  if (lock) {
    const gutter = Math.max(0, window.innerWidth - html.clientWidth);
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (gutter > 0) {
      html.style.paddingRight = `${gutter}px`;
      body.style.paddingRight = `${gutter}px`;
    }
    html.classList.add("subpage-modal-open");
    body.classList.add("subpage-modal-open");
  } else {
    html.style.overflow = "";
    body.style.overflow = "";
    html.style.paddingRight = "";
    body.style.paddingRight = "";
    html.classList.remove("subpage-modal-open");
    body.classList.remove("subpage-modal-open");
  }
}

type ExpandableCaseCardsProps = {
  initialId?: string | null;
};

export function ExpandableCaseCards({ initialId }: ExpandableCaseCardsProps) {
  const site = useContent();
  const cards = site.caseStudies;
  const [active, setActive] = useState<SiteCaseStudy | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (initialId) {
      const match = cards.find((c) => c.id === initialId);
      if (match) {
        setActive(match);
        return;
      }
    }
    try {
      const hash = window.location.hash.replace(/^#/, "");
      if (!hash) return;
      const match = cards.find((c) => c.id === hash);
      if (match) setActive(match);
    } catch {
      /* ignore */
    }
  }, [initialId, cards]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeCard();
    }
    lockPageScroll(Boolean(active));
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      lockPageScroll(false);
    };
    // closeCard is stable enough via setActive; avoid stale Escape
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useOutsideClick(ref, () => {
    if (active) closeCard();
  });

  function openCard(card: SiteCaseStudy) {
    setActive(card);
    try {
      const url = new URL(window.location.href);
      url.hash = card.id;
      window.history.replaceState({}, "", `${url.pathname}${url.search}#${card.id}`);
    } catch {
      /* ignore */
    }
  }

  function closeCard() {
    setActive(null);
    try {
      const url = new URL(window.location.href);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <AnimatePresence>
        {active ? (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE }}
            className={styles.backdrop}
            onClick={closeCard}
            aria-hidden
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {active ? (
          <div className={styles.modalLayer} role="dialog" aria-modal="true">
            <button
              type="button"
              className={styles.closeMobile}
              onClick={closeCard}
              aria-label={site.labels.close}
            >
              <CloseIcon />
            </button>
            <motion.div
              layoutId={`card-${active.id}-${id}`}
              ref={ref}
              className={styles.modal}
              transition={{ layout: LAYOUT_TRANSITION }}
            >
              <motion.div
                layoutId={`image-${active.id}-${id}`}
                className={styles.modalMedia}
                transition={{ layout: LAYOUT_TRANSITION }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={active.preview}
                  alt={active.previewAlt}
                  className={styles.modalImage}
                />
              </motion.div>
              <div className={styles.modalHeader}>
                <div>
                  <p className={styles.eyebrow}>{active.eyebrow}</p>
                  <h3 className={styles.modalTitle}>{active.title}</h3>
                  <p className={styles.modalSummary}>{active.summary}</p>
                </div>
                {active.url && active.url !== "#" ? (
                  <a
                    href={active.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.visit}
                  >
                    {site.labels.visitSite}
                  </a>
                ) : (
                  <button
                    type="button"
                    className={styles.visitGhost}
                    onClick={closeCard}
                  >
                    {site.labels.close}
                  </button>
                )}
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.22, delay: 0.08, ease: EASE }}
                className={styles.modalBody}
              >
                <div className={styles.block}>
                  <span className={styles.blockLabel}>{site.labels.problem}</span>
                  <p>{active.problem}</p>
                </div>
                <div className={styles.block}>
                  <span className={styles.blockLabel}>{site.labels.outcome}</span>
                  <p>{active.outcome}</p>
                </div>
                <div className={styles.block}>
                  <span className={styles.blockLabel}>{site.labels.stack}</span>
                  <ul className={styles.stack}>
                    {active.stack.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>

      <ul className={styles.list}>
        {cards.map((card, index) => (
          <motion.li
            layoutId={`card-${card.id}-${id}`}
            key={card.id}
            id={card.id}
            className={`${styles.card}${active?.id === card.id ? ` ${styles.cardOpen}` : ""}`}
            onClick={() => openCard(card)}
            transition={{ layout: LAYOUT_TRANSITION }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            // whileInView conflicts with cardOpen opacity — keep enter separate
            style={
              active?.id === card.id
                ? { opacity: 0, pointerEvents: "none" }
                : undefined
            }
          >
            <motion.div
              layoutId={`image-${card.id}-${id}`}
              className={styles.media}
              transition={{ layout: LAYOUT_TRANSITION }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={card.preview}
                alt={card.previewAlt}
                className={styles.mediaImage}
                loading={index < 4 ? "eager" : "lazy"}
              />
            </motion.div>
            <div className={styles.copy}>
              <p className={styles.eyebrow}>{card.eyebrow}</p>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              <p className={styles.cardSummary}>{card.summary}</p>
              <button type="button" className={styles.openBtn}>
                {site.labels.viewCaseStudy}
              </button>
            </div>
          </motion.li>
        ))}
      </ul>
    </>
  );
}
